<template>
  <section id="contact" class="contact bg-decoration">
    <div class="container">
      <div class="contact-content">
        <div class="contact-info">
          <SectionHeader subtitleKey="contact.subtitle" titleKey="contact.title" />
          <p class="contact-description">
            {{ $t('contact.description') }}
          </p>
          <div class="info-items">
            <div class="info-item glass glass-glow" v-for="(item, index) in contactItems" :key="index">
              <div class="info-icon glass-sm">
                <span v-html="item.icon"></span>
              </div>
              <div class="info-text">
                <h4>{{ $t(`contact.info.${item.key}.title`) }}</h4>
                <p>{{ $t(`contact.info.${item.key}.content`) }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="contact-form-wrapper glass glass-glow">
          <form class="contact-form" novalidate @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group">
                <label for="name">{{ $t('contact.form.name') }} <span class="required">*</span></label>
                <input
                  type="text"
                  id="name"
                  v-model="form.name"
                  :placeholder="$t('contact.form.namePlaceholder')"
                  :maxlength="fieldLimits.name"
                  required
                  @input="clearFieldError('name')"
                />
                <div class="field-meta">
                  <p v-if="fieldErrors.name" class="form-error">{{ fieldErrors.name }}</p>
                  <span class="char-count">{{ form.name.length }}/{{ fieldLimits.name }}</span>
                </div>
              </div>
              <div class="form-group">
                <label for="company">{{ $t('contact.form.company') }} <span class="required">*</span></label>
                <input
                  type="text"
                  id="company"
                  v-model="form.company"
                  :placeholder="$t('contact.form.companyPlaceholder')"
                  :maxlength="fieldLimits.company"
                  required
                  @input="clearFieldError('company')"
                />
                <div class="field-meta">
                  <p v-if="fieldErrors.company" class="form-error">{{ fieldErrors.company }}</p>
                  <span class="char-count">{{ form.company.length }}/{{ fieldLimits.company }}</span>
                </div>
              </div>
            </div>
            <div class="form-row three-cols">
              <div class="form-group">
                <label for="position">{{ $t('contact.form.position') }} <span class="required">*</span></label>
                <input
                  type="text"
                  id="position"
                  v-model="form.position"
                  :placeholder="$t('contact.form.positionPlaceholder')"
                  :maxlength="fieldLimits.position"
                  required
                  @input="clearFieldError('position')"
                />
                <div class="field-meta">
                  <p v-if="fieldErrors.position" class="form-error">{{ fieldErrors.position }}</p>
                  <span class="char-count">{{ form.position.length }}/{{ fieldLimits.position }}</span>
                </div>
              </div>
              <div class="form-group">
                <label for="phone">{{ $t('contact.form.phone') }} <span class="required">*</span></label>
                <input
                  type="tel"
                  id="phone"
                  v-model="form.phone"
                  :placeholder="$t('contact.form.phonePlaceholder')"
                  :minlength="fieldLimits.phoneMin"
                  :maxlength="fieldLimits.phoneMax"
                  required
                  @input="handlePhoneInput"
                />
                <div class="field-meta">
                  <p v-if="fieldErrors.phone" class="form-error">{{ fieldErrors.phone }}</p>
                  <span class="char-count">{{ form.phone.length }}/{{ fieldLimits.phoneMax }}</span>
                </div>
              </div>
              <div class="form-group">
                <label for="email">{{ $t('contact.form.email') }} <span class="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  v-model="form.email"
                  :placeholder="$t('contact.form.emailPlaceholder')"
                  :maxlength="fieldLimits.email"
                  required
                  @input="handleEmailInput"
                />
                <div class="field-meta">
                  <p v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</p>
                  <span class="char-count">{{ form.email.length }}/{{ fieldLimits.email }}</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="remarks">{{ $t('contact.form.remarks') }}</label>
              <textarea
                id="remarks"
                v-model="form.remarks"
                rows="5"
                :placeholder="$t('contact.form.remarksPlaceholder')"
                :maxlength="fieldLimits.remarks"
                @input="clearFieldError('remarks')"
              ></textarea>
              <div class="field-meta">
                <p v-if="fieldErrors.remarks" class="form-error">{{ fieldErrors.remarks }}</p>
                <span class="char-count">{{ form.remarks.length }}/{{ fieldLimits.remarks }}</span>
              </div>
            </div>
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ isSubmitting ? $t('contact.form.submitting') : $t('contact.form.submit') }}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SectionHeader from './common/SectionHeader.vue'
import { iconMap } from '@/assets/icons.js'
import { submitCustomerContact } from '@/api/websiteContact'
import { isEmailCharactersValid, isPhoneCharactersValid, sanitizeEmail, sanitizePhone } from '@/utils/contactForm'
import { showToast } from '@/composables/useToast'

const { t } = useI18n()
const isSubmitting = ref(false)
const fieldLimits = {
  name: 20,
  company: 32,
  position: 32,
  phoneMin: 6,
  phoneMax: 20,
  email: 32,
  remarks: 2000
}
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const form = reactive({
  name: '',
  company: '',
  position: '',
  phone: '',
  email: '',
  remarks: ''
})

const fieldErrors = reactive({
  name: '',
  company: '',
  position: '',
  phone: '',
  email: '',
  remarks: ''
})

const contactItems = ref([
  {
    key: 'address',
    icon: iconMap.address
  },
  {
    key: 'phone',
    icon: iconMap.phone
  },
  {
    key: 'email',
    icon: iconMap.email
  }
])

const clearFieldError = (field) => {
  fieldErrors[field] = ''
}

const handlePhoneInput = () => {
  form.phone = sanitizePhone(form.phone)
  clearFieldError('phone')
}

const handleEmailInput = () => {
  form.email = sanitizeEmail(form.email)
  clearFieldError('email')
}

const trimForm = () => {
  form.name = form.name.trim()
  form.company = form.company.trim()
  form.position = form.position.trim()
  form.phone = form.phone.trim()
  form.email = form.email.trim()
  form.remarks = form.remarks.trim()
}

const resetForm = () => {
  Object.assign(form, {
    name: '',
    company: '',
    position: '',
    phone: '',
    email: '',
    remarks: ''
  })
}

const validateRequiredAndMaxLength = (field, maxLength, requiredKey, maxKey) => {
  const value = form[field].trim()
  if (!value) {
    fieldErrors[field] = t(requiredKey)
    return false
  }
  if (value.length > maxLength) {
    fieldErrors[field] = t(maxKey, { max: maxLength })
    return false
  }
  return true
}

const validateForm = () => {
  trimForm()
  Object.keys(fieldErrors).forEach((field) => {
    fieldErrors[field] = ''
  })

  let valid = true
  valid = validateRequiredAndMaxLength('name', fieldLimits.name, 'contact.form.nameRequired', 'contact.form.nameMaxLength') && valid
  valid = validateRequiredAndMaxLength('company', fieldLimits.company, 'contact.form.companyRequired', 'contact.form.companyMaxLength') && valid
  valid = validateRequiredAndMaxLength('position', fieldLimits.position, 'contact.form.positionRequired', 'contact.form.positionMaxLength') && valid

  if (!form.phone) {
    fieldErrors.phone = t('contact.form.phoneRequired')
    valid = false
  } else if (!isPhoneCharactersValid(form.phone)) {
    fieldErrors.phone = t('contact.form.phoneInvalidChars')
    valid = false
  } else if (form.phone.length < fieldLimits.phoneMin || form.phone.length > fieldLimits.phoneMax) {
    fieldErrors.phone = t('contact.form.phoneLength', {
      min: fieldLimits.phoneMin,
      max: fieldLimits.phoneMax
    })
    valid = false
  }

  if (!form.email) {
    fieldErrors.email = t('contact.form.emailRequired')
    valid = false
  } else if (!isEmailCharactersValid(form.email)) {
    fieldErrors.email = t('contact.form.emailInvalidChars')
    valid = false
  } else if (form.email.length > fieldLimits.email) {
    fieldErrors.email = t('contact.form.emailMaxLength', { max: fieldLimits.email })
    valid = false
  } else if (!emailPattern.test(form.email)) {
    fieldErrors.email = t('contact.form.emailFormat')
    valid = false
  }

  if (form.remarks.length > fieldLimits.remarks) {
    fieldErrors.remarks = t('contact.form.remarksMaxLength', { max: fieldLimits.remarks })
    valid = false
  }

  return valid
}

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return
  }
  if (!validateForm()) {
    return
  }

  try {
    isSubmitting.value = true
    await submitCustomerContact({
      name: form.name,
      company: form.company,
      position: form.position,
      mobile: form.phone,
      email: form.email,
      remark: form.remarks
    })
    showToast(t('contact.alert'))
    resetForm()
  } catch (error) {
    showToast(error.message || t('contact.form.errorMessage'), 'error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.contact {
  padding: 120px 0;
  background: linear-gradient(135deg, #e8f0f7 0%, #f1f5f9 50%, #f8fafc 100%);
  position: relative;
}

.contact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 75% 25%, rgba(52, 152, 255, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 25% 75%, rgba(15, 39, 64, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 80px;
  position: relative;
  z-index: 1;
}

.contact-description {
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 36px;
  font-size: 1.05rem;
}

.info-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-item {
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 20px;
  border-radius: 18px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-item:hover {
  transform: translateX(6px);
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(52, 152, 255, 0.15) 0%, rgba(15, 39, 64, 0.08) 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(52, 152, 255, 0.1);
}

.info-icon svg {
  width: 28px;
  height: 28px;
}

.info-text h4 {
  font-size: 1rem;
  color: var(--primary-color);
  margin-bottom: 4px;
  font-weight: 700;
}

.info-text p {
  color: var(--text-light);
  margin: 0;
  font-size: 0.98rem;
}

.contact-form-wrapper {
  padding: 48px;
  border-radius: 28px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.contact-form-wrapper:hover {
  transform: translateY(-4px);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-row.three-cols {
  grid-template-columns: repeat(3, 1fr);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-size: 0.95rem;
  color: var(--text-dark);
  font-weight: 600;
}

.form-group label .required {
  color: #e74c3c;
  margin-left: 4px;
  font-weight: 700;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.6);
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 4px rgba(52, 152, 255, 0.15), 0 4px 12px rgba(52, 152, 255, 0.1);
}

.field-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
}

.form-error {
  margin: 0;
  color: #e74c3c !important;
  font-size: 0.84rem;
  line-height: 1.5;
}

.char-count {
  margin-left: auto;
  color: var(--text-light);
  font-size: 0.82rem;
  line-height: 1.5;
  white-space: nowrap;
}

.submit-btn {
  padding: 16px 40px;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: var(--white);
  border: none;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.3);
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.submit-btn:hover {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.4);
}

.submit-btn:active {
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: submitSpin 0.8s linear infinite;
}

@keyframes submitSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .contact-content {
    grid-template-columns: 1fr;
    gap: 50px;
  }
  .contact-info {
    order: -1;
  }
}

@media (max-width: 768px) {
  .contact {
    padding: 80px 0;
  }
  .contact-content {
    gap: 40px;
  }
  .contact-description {
    font-size: 1rem;
    margin-bottom: 30px;
  }
  .info-items {
    gap: 16px;
  }
  .info-item {
    padding: 18px;
  }
  .info-icon {
    width: 52px;
    height: 52px;
  }
  .contact-form-wrapper {
    padding: 32px 24px;
  }
  .form-row {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .submit-btn {
    width: 100%;
    padding: 14px 30px;
    font-size: 1.02rem;
    font-weight: 700;
  }
  .form-group label {
    font-size: 0.92rem;
  }
  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.98rem;
    padding: 12px 16px;
  }
}
</style>
